import React from 'react'
import Skeleton from 'components/skeleton'
import PageHeader from 'components/pageHeader'
import { PAGE_HEADER_ACTIONS, PAGE_HEADER_GROUP } from 'components/pageHeader/styles'
import { HEADER_HEIGHT, ROW_NUMBER_COLUMN_WIDTH, SMALL_ROW_HEIGHT, TOOLBAR_HEIGHT_CLASS } from 'style/constants'

// What a table shows until its first rows arrive: the shape of the loaded page
// (header, view tabs, grid) in placeholders, so the page doesn't change layout
// when the data lands.

// Widths for the placeholder bars, cycled so neighbouring rows and columns
// differ the way real names and values do.
const TAB_WIDTHS = [88, 64, 76]
const VIEW_ROW_GROUP_SIZES = [2, 3, 2, 2]
const COLUMN_WIDTHS = [180, 140, 160, 120, 200, 140, 160, 120, 180, 140, 160, 120]
const CELL_FILLS = ['70%', '45%', '85%', '55%', '65%', '40%', '75%']
const ROWS = 40

// An icon button's placeholder: the 16px icon in the button's 28px square, so
// a group of them is spaced as the buttons are.
const IconSkeleton = () => (
  <span className="flex items-center justify-center h-7 w-7">
    <Skeleton className="block h-4 w-4 rounded" />
  </span>
)

/** The table page's toolbar while it loads: the page header and view row, or, inside a document, the one-row toolbar. */
export const ToolbarSkeleton: React.FC<{ inProcess: boolean }> = ({ inProcess }) => {
  if (inProcess) {
    return (
      <div
        className={`flex w-full shrink-0 items-center gap-3 box-border border-b border-divider px-3 ${TOOLBAR_HEIGHT_CLASS}`}
        data-testid="spreadsheet-toolbar-loading"
        aria-hidden
      >
        <Skeleton className="block h-4 w-[160px]" />
        <Skeleton className="block h-4 w-[96px]" />
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full shrink-0 box-border select-none" data-testid="spreadsheet-toolbar-loading">
      <PageHeader name="" testIdPrefix="table-loading" loading>
        <div className={PAGE_HEADER_ACTIONS} aria-hidden>
          <div className={PAGE_HEADER_GROUP}>
            <IconSkeleton />
            <IconSkeleton />
          </div>
          <div className={PAGE_HEADER_GROUP}>
            <IconSkeleton />
          </div>
        </div>
      </PageHeader>
      <div
        className="flex items-center w-full h-10 px-[5px] bg-surface shadow-[inset_0_-1px_0_var(--color-divider)]"
        aria-hidden
      >
        <div className="flex items-center gap-4 flex-1 min-w-0 overflow-hidden px-2">
          {TAB_WIDTHS.map((width, index) => (
            <div key={index} className="flex items-center gap-1.5 shrink-0">
              <Skeleton className="block h-4 w-4 rounded" />
              <Skeleton className="block h-3" style={{ width }} />
            </div>
          ))}
        </div>
        <div className={`${PAGE_HEADER_ACTIONS} pr-2 max-md:hidden`}>
          {VIEW_ROW_GROUP_SIZES.map((size, group) => (
            <div key={group} className={PAGE_HEADER_GROUP}>
              {Array.from({ length: size }, (_, index) => (
                <IconSkeleton key={index} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * The grid while the table loads: a header row and rows of placeholder cells,
 * with whatever progress there is to report (`children`) on top.
 */
export const GridSkeleton: React.FC<{ className: string; children?: React.ReactNode }> = ({ className, children }) => (
  <div
    data-testid="spreadsheet-loading"
    role="status"
    aria-label="Loading table"
    className={`relative w-full overflow-hidden bg-surface z-[2] ${className}`}
  >
    {/* Fades out down the grid: it's a placeholder, not a promise of rows. */}
    <div className="absolute inset-0 [mask-image:linear-gradient(to_bottom,black_50%,transparent)]" aria-hidden>
      <div className="flex border-b border-divider bg-surface-sunken" style={{ height: HEADER_HEIGHT }}>
        <div className="shrink-0 border-r border-divider" style={{ width: ROW_NUMBER_COLUMN_WIDTH }} />
        {COLUMN_WIDTHS.map((width, column) => (
          <div
            key={column}
            className="flex items-center gap-1.5 shrink-0 px-2 border-r border-divider"
            style={{ width }}
          >
            <Skeleton className="block h-3.5 w-3.5 rounded" />
            <Skeleton className="block h-3" style={{ width: CELL_FILLS[column % CELL_FILLS.length] }} />
          </div>
        ))}
      </div>
      <div>
        {Array.from({ length: ROWS }, (_, row) => (
          <div key={row} className="flex border-b border-divider" style={{ height: SMALL_ROW_HEIGHT }}>
            <div
              className="flex items-center justify-center shrink-0 border-r border-divider bg-surface-sunken"
              style={{ width: ROW_NUMBER_COLUMN_WIDTH }}
            >
              <Skeleton className="block h-2.5 w-4" />
            </div>
            {COLUMN_WIDTHS.map((width, column) => (
              <div key={column} className="flex items-center shrink-0 px-2 border-r border-divider" style={{ width }}>
                <Skeleton
                  className="block h-2.5"
                  style={{ width: CELL_FILLS[(row + column * 3) % CELL_FILLS.length] }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
    {children && (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="flex flex-col items-center gap-2 w-[280px] px-5 py-4 rounded-lg bg-surface shadow-lg border border-divider">
          {children}
        </div>
      </div>
    )}
  </div>
)
